import React, { useEffect, useState, useCallback } from "react";
import {
  Grid2x2,
  Layers,
  Paintbrush,
  Trees,
  Bath,
  Home as HomeIcon,
  Gem,
  KeyRound,
  Hammer,
  ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ZoomableImage from "@/components/site/ZoomableImage";

// To attach photos to a service, drop files under /public/images/services/<slug>/
// and list them in the `photos` array below.
const SERVICES = [
  {
    icon: KeyRound,
    title: "Renovări Complete Case și Apartamente",
    text:
      "Renovări la cheie pentru case și apartamente: demolări, instalații, finisaje și decor. Un singur partener, de la proiect la predare.",
    photos: ["/images/services/renovari-complete/01.webp"],
  },
  {
    icon: Paintbrush,
    title: "Tencuială",
    text:
      "Tencuieli pe bază de ipsos și de ciment, tencuieli decorative interioare și exterioare. Pregătim pereții pentru un finisaj premium.",
    photos: [
      "/images/services/tencuiala/01.webp",
      "/images/services/tencuiala/02.jpg",
    ],
  },
  {
    icon: Layers,
    title: "Gipscarton",
    text:
      "Pereți compartimentare, tavane false, scafe decorative cu iluminare LED, nișe și placări — soluții estetice și rezistente.",
    photos: [
      "/images/services/gipscarton/01.jpg",
      "/images/services/gipscarton/02.jpg",
    ],
  },
  {
    icon: Grid2x2,
    title: "Gresie și Faianță",
    text:
      "Montaj profesional de gresie și faianță în băi, bucătării și spații comerciale. Aliniere perfectă, rosturi curate, finisaje impecabile.",
    photos: [
      "/images/services/gresie-faianta/01.jpg",
      "/images/services/gresie-faianta/02.jpg",
      "/images/services/gresie-faianta/03.jpg",
    ],
  },
  {
    icon: Trees,
    title: "Amenajări Exterioare",
    text:
      "Pavaje, alei, curți interioare și terase. Montaj pavele, borduri, drenaje și finisaje exterioare durabile, plăcări termosistem complet.",
    photos: [
      "/images/services/amenajari-exterioare/01.webp",
      "/images/services/amenajari-exterioare/02.webp",
      "/images/services/amenajari-exterioare/03.webp",
    ],
  },
  {
    icon: Bath,
    title: "Amenajări Baie Completă",
    text:
      "Renovări complete de baie: instalații sanitare, hidroizolații, gresie, faianță, montaj obiecte sanitare și mobilier — la cheie.",
    photos: [
      "/images/services/baie-completa/01.webp",
      "/images/services/baie-completa/02.webp",
      "/images/services/baie-completa/03.jpg",
    ],
  },
  {
    icon: HomeIcon,
    title: "Montaj Parchet Laminat",
    text:
      "Montaj parchet laminat și stratificat, cu pregătire riguroasă a șapei, izolație fonică și profile de finisaj potrivite spațiului.",
    photos: ["/images/services/parchet/01.webp"],
  },
  {
    icon: Gem,
    title: "Finisaje Piatră / Marmură",
    text:
      "Placări cu marmură și piatră naturală pentru scări, trepte, șemineuri. Detalii fine, execuție de precizie.",
    photos: ["/images/services/piatra-marmura/01.jpg"],
  },
  {
    icon: Hammer,
    title: "Decopertări",
    text:
      "Îndepărtăm tencuiala, gresia, șapa sau vopseaua veche de pe pereți și pardoseli, pregătind suprafața corect pentru un finisaj nou, de calitate.",
    photos: ["/images/services/decopertari/01.webp"],
  },
];

export default function Services() {
  const [openIdx, setOpenIdx] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  const close = useCallback(() => setOpenIdx(null), []);
  const openService = openIdx !== null ? SERVICES[openIdx] : null;
  const photosLen = openService?.photos?.length || 0;
  const prev = useCallback(
    () => setPhotoIdx((i) => (photosLen ? (i - 1 + photosLen) % photosLen : 0)),
    [photosLen]
  );
  const next = useCallback(
    () => setPhotoIdx((i) => (photosLen ? (i + 1) % photosLen : 0)),
    [photosLen]
  );

  const openAt = (idx) => {
    setOpenIdx(idx);
    setPhotoIdx(0);
  };

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, close, prev, next]);

  return (
    <section
      id="servicii"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-x">
        <div className="max-w-2xl reveal">
          <div className="eyebrow mb-5">
            <span>Serviciile noastre</span>
          </div>
          <h2 className="font-display text-[var(--text-primary)] text-4xl md:text-5xl leading-[1.08] font-medium">
            Tot ce ai nevoie pentru o renovare
            <span className="text-brand-blue italic"> completă</span>.
          </h2>
          <p className="mt-5 text-[var(--text-secondary)] text-base md:text-lg max-w-xl">
            Executăm întregul flux al unei lucrări — de la structură și
            instalații, până la finisajele de detaliu. Echipă proprie, materiale
            verificate, termene respectate.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${idx}`}
                className="service-card reveal flex flex-col"
                style={{ transitionDelay: `${(idx % 3) * 60}ms` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[rgba(30,95,217,0.10)] border border-[rgba(30,95,217,0.35)] flex items-center justify-center">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.2} />
                  </div>
                  <span className="num-badge">0{idx + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-xl md:text-[1.35rem] text-[var(--text-primary)] leading-snug font-medium">
                  {s.title}
                </h3>
                <p className="mt-3 text-[var(--text-secondary)] text-sm md:text-[15px] leading-relaxed">
                  {s.text}
                </p>
                <button
                  type="button"
                  onClick={() => openAt(idx)}
                  data-testid={`service-photo-btn-${idx}`}
                  className="mt-6 self-start btn-gold text-sm px-5 py-2.5"
                >
                  <ImageIcon className="h-4 w-4" strokeWidth={2.4} />
                  Vezi fotografie
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border-t border-[var(--border-subtle)] pt-8 reveal">
          <p className="text-[var(--text-secondary)] max-w-lg">
            Ai un proiect specific?{" "}
            <span className="text-[var(--text-primary)]">Vino cu o idee, plecăm cu o soluție.</span>
          </p>
          <div className="flex gap-3">
            <a
              href="tel:0746329111"
              data-testid="services-call-btn"
              className="btn-gold"
            >
              Sună acum
            </a>
            <a
              href="https://wa.me/40746329111"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-whatsapp-btn"
              className="btn-whatsapp"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Service photo slideshow lightbox */}
      {openService && (
        <div
          data-testid="service-photo-lightbox"
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={close}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            data-testid="service-photo-close"
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors focus-ring"
            aria-label="Închide"
          >
            <X className="h-5 w-5" />
          </button>

          {photosLen > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                data-testid="service-photo-prev"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-13 md:w-13 rounded-full bg-black/55 hover:bg-black/75 border border-white/40 flex items-center justify-center text-white shadow-xl backdrop-blur transition-colors focus-ring z-10"
                aria-label="Fotografia anterioară"
              >
                <ChevronLeft className="h-6 w-6" strokeWidth={2.4} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                data-testid="service-photo-next"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-13 md:w-13 rounded-full bg-black/55 hover:bg-black/75 border border-white/40 flex items-center justify-center text-white shadow-xl backdrop-blur transition-colors focus-ring z-10"
                aria-label="Fotografia următoare"
              >
                <ChevronRight className="h-6 w-6" strokeWidth={2.4} />
              </button>
            </>
          )}

          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-4xl w-full rounded-2xl overflow-hidden border border-white/10 bg-[var(--bg-card)]"
          >
            <div className="aspect-[4/3] w-full flex items-center justify-center">
              {photosLen > 0 ? (
                <ZoomableImage
                  src={openService.photos[photoIdx]}
                  alt={`${openService.title} — fotografia ${photoIdx + 1}`}
                />
              ) : (
                <div className="text-center flex flex-col items-center gap-3 px-6">
                  <ImageIcon
                    className="h-14 w-14 text-[var(--text-muted)]"
                    strokeWidth={1.2}
                  />
                  <div className="font-display text-[var(--text-primary)] text-lg md:text-xl">
                    {openService.title}
                  </div>
                  <div className="text-sm text-[var(--text-muted)]">
                    Fotografia va fi disponibilă în curând.
                  </div>
                </div>
              )}
            </div>
          </div>

          {photosLen > 1 && (
            <div
              data-testid="service-photo-counter"
              className="absolute bottom-5 md:bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/55 backdrop-blur text-white text-sm font-medium"
            >
              {photoIdx + 1} / {photosLen}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
